import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Statistic, Progress, message, Table, Tag, Badge } from "antd";
import {
    HeartOutlined,
    AlertOutlined,
    CheckCircleOutlined,
    InboxOutlined,
    UserOutlined,
    FileTextOutlined,
    BellOutlined,
    TeamOutlined,
    MedicineBoxOutlined,
    BankOutlined,
    EnvironmentOutlined
} from "@ant-design/icons";
import { oneKlicks } from "../../SDK/oneclick";
import { getuserdata } from "../../services/apiService";

const BloodBankDashboard = () => {
    const [userdata, setUserdata] = useState({});
    const [bloodGroups, setBloodGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalGroups: 0,
        totalUnits: 0,
        lowStock: 0,
        available: 0
    });

    // New states for additional data
    const [donors, setDonors] = useState([]);
    const [requests, setRequests] = useState([]);
    const [nearbyHospitals, setNearbyHospitals] = useState([]);
    const [nearbyBloodBanks, setNearbyBloodBanks] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem("userData") || "{}");
            setUserdata(userData);

            const userId = await getuserdata('id');

            // Fetch blood groups for this blood bank
            const bloodGroupsRes = await oneKlicks.read('', 'blood_groups', { blood_bank_id: userId }, 'id DESC');
            if (bloodGroupsRes && bloodGroupsRes.data) {
                setBloodGroups(bloodGroupsRes.data);

                const totalUnits = bloodGroupsRes.data.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
                const lowStock = bloodGroupsRes.data.filter(item => (parseInt(item.quantity) || 0) < 5).length;
                const available = bloodGroupsRes.data.filter(item => (parseInt(item.quantity) || 0) > 0).length;

                setStats({
                    totalGroups: bloodGroupsRes.data.length,
                    totalUnits,
                    lowStock,
                    available
                });
            }

            // Fetch donors
            const donorsRes = await oneKlicks.read('', 'blood_donors', { blood_bank_id: userId }, 'id DESC', '', { page: 1, limit: 5 });
            if (donorsRes && donorsRes.data) {
                setDonors(donorsRes.data);
            }

            // Fetch blood requests
            const requestsRes = await oneKlicks.read('', 'blood_requests', { blood_bank_id: userId }, 'id DESC', '', { page: 1, limit: 5 });
            if (requestsRes && requestsRes.data) {
                setRequests(requestsRes.data);
            }

            // Fetch nearby hospitals
            const hospitalsRes = await oneKlicks.read('', 'users', { user_type: 'hospital' }, 'id DESC', '', { page: 1, limit: 5 });
            if (hospitalsRes && hospitalsRes.data) {
                setNearbyHospitals(hospitalsRes.data);
            }

            // Fetch nearby blood banks
            const bloodBanksRes = await oneKlicks.read('', 'blood_bank', {}, 'id DESC', '', { page: 1, limit: 5 });
            if (bloodBanksRes && bloodBanksRes.data) {
                setNearbyBloodBanks(bloodBanksRes.data.filter(bb => bb.id !== userId).slice(0, 4));
            }

        } catch (error) {
            console.error('Dashboard load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const bloodGroupColors = {
        'A+': '#ff4d4f',
        'A-': '#ff7875',
        'B+': '#1890ff',
        'B-': '#69c0ff',
        'AB+': '#722ed1',
        'AB-': '#b37feb',
        'O+': '#52c41a',
        'O-': '#95de64'
    };

    const statusColors = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red',
        fulfilled: 'blue'
    };

    const donorColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Blood Group',
            dataIndex: 'blood_group',
            key: 'blood_group',
            render: (group) => <Tag color={bloodGroupColors[group] ? 'red' : 'default'}>{group}</Tag>
        },
        { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    ];

    const requestColumns = [
        { title: 'Patient', dataIndex: 'patient_name', key: 'patient_name' },
        {
            title: 'Blood Group',
            dataIndex: 'blood_group',
            key: 'blood_group',
            render: (group) => <Tag color="red">{group}</Tag>
        },
        { title: 'Units', dataIndex: 'units_required', key: 'units_required' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => <Tag color={statusColors[status] || 'default'}>{status || 'Pending'}</Tag>
        },
    ];

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <h4 className="mb-4 fw-bold">
                <HeartOutlined className="me-2 text-danger" />
                Blood Bank Dashboard
            </h4>

            {/* Stats Cards - Similar to CardViews style */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#1890ff', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.totalGroups}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <InboxOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Blood Groups</h5>
                            <Link to="/blood-bank/blood-groups" style={{ fontSize: '12px' }}>Manage Groups</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#52c41a', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.totalUnits}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <HeartOutlined style={{ fontSize: '32px', color: '#52c41a' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Total Units</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Available in stock</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#ff4d4f', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{stats.lowStock}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <AlertOutlined style={{ fontSize: '32px', color: '#ff4d4f' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Low Stock</h5>
                            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>Need attention</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#722ed1', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{donors.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <UserOutlined style={{ fontSize: '32px', color: '#722ed1' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Donors</h5>
                            <Link to="/blood-bank/donors" style={{ fontSize: '12px' }}>View All Donors</Link>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Second Row of Cards */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#faad14', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{requests.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <FileTextOutlined style={{ fontSize: '32px', color: '#faad14' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Blood Requests</h5>
                            <Link to="/blood-bank/requests" style={{ fontSize: '12px' }}>View Requests</Link>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#13c2c2', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{nearbyHospitals.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <MedicineBoxOutlined style={{ fontSize: '32px', color: '#13c2c2' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Nearby Hospitals</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Partner hospitals</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <span><em className="dcunread-count" style={{ background: '#eb2f96', color: '#fff', padding: '5px 12px', borderRadius: '20px' }}>{nearbyBloodBanks.length}</em></span>
                        <div style={{ marginTop: '15px' }}>
                            <BankOutlined style={{ fontSize: '32px', color: '#eb2f96' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Other Blood Banks</h5>
                            <span style={{ fontSize: '12px', color: '#666' }}>Nearby banks</span>
                        </div>
                    </div>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <div className="dc-insightsitem dc-dashboardbox" style={{ background: '#fff', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ marginTop: '15px' }}>
                            <BellOutlined style={{ fontSize: '32px', color: '#f5222d' }} />
                            <h5 style={{ marginTop: '10px', marginBottom: '5px' }}>Notifications</h5>
                            <Link to="/blood-bank/notifications" style={{ fontSize: '12px' }}>View All</Link>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Blood Groups Inventory Overview */}
            <Card title={<><HeartOutlined className="me-2" />Blood Inventory Overview</>} className="shadow-sm border-0 mb-4">
                <Row gutter={[16, 16]}>
                    {bloodGroups.length > 0 ? (
                        bloodGroups.map((group, index) => (
                            <Col xs={12} sm={8} md={6} lg={3} key={index}>
                                <Card
                                    className="text-center shadow-sm"
                                    style={{
                                        borderTop: `3px solid ${bloodGroupColors[group.group_name] || '#1890ff'}`
                                    }}
                                >
                                    <div style={{
                                        fontSize: '24px',
                                        fontWeight: 'bold',
                                        color: bloodGroupColors[group.group_name] || '#1890ff'
                                    }}>
                                        {group.group_name}
                                    </div>
                                    <div className="text-muted mt-2">
                                        {group.quantity || 0} {group.unit || 'units'}
                                    </div>
                                    <Progress
                                        percent={Math.min((parseInt(group.quantity) / 50) * 100, 100)}
                                        size="small"
                                        strokeColor={bloodGroupColors[group.group_name] || '#1890ff'}
                                        showInfo={false}
                                        className="mt-2"
                                    />
                                </Card>
                            </Col>
                        ))
                    ) : (
                        <Col span={24}>
                            <div className="text-center text-muted py-5">
                                <InboxOutlined style={{ fontSize: '48px', opacity: 0.3 }} />
                                <p className="mt-3">No blood groups added yet. Go to Blood Groups to add inventory.</p>
                            </div>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* Donors and Requests Tables */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} md={12}>
                    <Card
                        title={<><UserOutlined className="me-2" />Recent Donors</>}
                        className="shadow-sm border-0"
                        extra={<Link to="/blood-bank/donors">View All</Link>}
                    >
                        <Table
                            columns={donorColumns}
                            dataSource={donors}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: 'No donors yet' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={<><FileTextOutlined className="me-2" />Recent Blood Requests</>}
                        className="shadow-sm border-0"
                        extra={<Link to="/blood-bank/requests">View All</Link>}
                    >
                        <Table
                            columns={requestColumns}
                            dataSource={requests}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            locale={{ emptyText: 'No requests yet' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Nearby Hospitals and Blood Banks */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title={<><MedicineBoxOutlined className="me-2" />Nearby Hospitals</>}
                        className="shadow-sm border-0"
                    >
                        {nearbyHospitals.length > 0 ? (
                            nearbyHospitals.map((hospital, index) => (
                                <div key={index} style={{ padding: '10px 0', borderBottom: index < nearbyHospitals.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <MedicineBoxOutlined style={{ fontSize: '24px', color: '#13c2c2', marginRight: '12px' }} />
                                        <div>
                                            <strong>{hospital.display_name || hospital.name}</strong>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <EnvironmentOutlined className="me-1" />
                                                {hospital.address || 'Address not available'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted py-4">No nearby hospitals found</div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={<><BankOutlined className="me-2" />Other Blood Banks</>}
                        className="shadow-sm border-0"
                    >
                        {nearbyBloodBanks.length > 0 ? (
                            nearbyBloodBanks.map((bank, index) => (
                                <div key={index} style={{ padding: '10px 0', borderBottom: index < nearbyBloodBanks.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <BankOutlined style={{ fontSize: '24px', color: '#eb2f96', marginRight: '12px' }} />
                                        <div>
                                            <strong>{bank.name || bank.display_name}</strong>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <EnvironmentOutlined className="me-1" />
                                                {bank.address || 'Address not available'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center text-muted py-4">No other blood banks found</div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default BloodBankDashboard;
